import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, PhoneIcon, MapPinIcon, MailIcon } from 'lucide-react';
import { COMPANY } from '../../lib/site';
import { SectionHeading } from '../ui/SectionHeading';
import { LazyMap } from '../ui/LazyMap';
import { Reveal } from '../fx/Reveal';
type Status = 'idle' | 'done';
type FieldName = 'name' | 'email' | 'phone';
type ContactForm = {
  name: string;
  email: string;
  phone: string;
  message: string;
};
const fields: Array<{
  name: FieldName;
  label: string;
  type: string;
  placeholder: string;
}> = [{
  name: 'name',
  label: 'Full Name',
  type: 'text',
  placeholder: 'Jane Doe'
}, {
  name: 'email',
  label: 'Email',
  type: 'email',
  placeholder: 'jane@email.com'
}, {
  name: 'phone',
  label: 'Phone',
  type: 'tel',
  placeholder: '(645) 216-5704'
}];
const houstonEmbedUrl = 'https://www.openstreetmap.org/export/embed.html?bbox=-95.8%2C29.5%2C-95.0%2C30.1&layer=mapnik&marker=29.7604%2C-95.3698';
const houstonMapsUrl = 'https://www.google.com/maps/search/?api=1&query=Houston%2C%20Texas';
export function ContactSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [form, setForm] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (status !== 'idle') return;
    const subject = `New inspection request from ${form.name}`;
    const body = ['New Dream Shield website lead', '', `Name: ${form.name}`, `Email: ${form.email}`, `Phone: ${form.phone || 'Not provided'}`, '', 'Request:', form.message].join('\n');
    setStatus('done');
    window.location.href = `${COMPANY.emailHref}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.setTimeout(() => {
      setStatus('idle');
      setForm({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    }, 2500);
  };
  return <section className="deferred-section relative mx-auto max-w-7xl px-6 py-24" data-id="element-177" data-mp="2s5k8fukojuff">
      <SectionHeading eyebrow="Get In Touch" title="Let's Protect Your Home" subtitle="Schedule a free inspection or reach us any time for an emergency. We respond fast." data-id="element-178" data-mp="2o3gumzajgdqz" />

      <div className="mt-14 grid gap-8 lg:grid-cols-2" data-id="element-179" data-mp="2x3ddule5sken">
        <Reveal data-id="element-180" data-mp="2x1sjowvrb3hb">
          <div className="glass-strong flex h-full flex-col overflow-hidden rounded-[2.5rem] shadow-glass" data-id="element-181" data-mp="3bsi0cerzlask">
            <LazyMap title="Dream Shield service area map" embedUrl={houstonEmbedUrl} mapsUrl={houstonMapsUrl} className="h-64 w-full lg:h-80" data-id="element-182" data-mp="36toriznna7yq" />
            <div className="grid flex-1 gap-4 p-8" data-id="element-183" data-mp="3ftv3gh3p2zvs">
              <a href={COMPANY.phoneHref} className="flex items-center gap-4 rounded-2xl bg-white/60 p-4 transition-colors hover:bg-white" data-id="element-184" data-mp="3fs0g6w4yavoj">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-fire/10 text-fire" data-id="element-185" data-mp="1lhwtue1h41zd">
                  <PhoneIcon className="h-5 w-5" data-id="element-186" data-mp="10dwj4jrwvar" />
                </span>
                <span data-id="element-187" data-mp="5wmk96c1dwtj">
                  <span className="block text-xs uppercase tracking-wider text-slate" data-id="element-188" data-mp="5z8s772uaeca">
                    24/7 Phone
                  </span>
                  <span className="font-semibold text-navy" data-id="element-189" data-mp="3sbxi5fhc3j9x">
                    {COMPANY.phoneDisplay}
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl bg-white/60 p-4" data-id="element-190" data-mp="3jc0y5r5w38l7">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-royal/10 text-royal" data-id="element-191" data-mp="3j9y1wpe58j81">
                  <MapPinIcon className="h-5 w-5" data-id="element-192" data-mp="39duzrjjakkl3" />
                </span>
                <span data-id="element-193" data-mp="39e4n0zcycdn3">
                  <span className="block text-xs uppercase tracking-wider text-slate" data-id="element-194" data-mp="13kwl3i036ej2">
                    Service Area
                  </span>
                  <span className="font-semibold text-navy" data-id="element-195" data-mp="17l6rgvrsj6yg">
                    {COMPANY.serviceArea}
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-4 rounded-2xl bg-white/60 p-4" data-id="element-196" data-mp="dw7o14w7mbrs">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-success/10 text-success" data-id="element-197" data-mp="8xnzqxu58nw9">
                  <MailIcon className="h-5 w-5" data-id="element-198" data-mp="3z2zwzit068c" />
                </span>
                <span data-id="element-199" data-mp="40xocntaagh6">
                  <span className="block text-xs uppercase tracking-wider text-slate" data-id="element-200" data-mp="3w2vtivv690xz">
                    Email
                  </span>
                  <a href={COMPANY.emailHref} className="font-semibold text-navy transition-colors hover:text-royal" data-id="element-201" data-mp="3ra2oe6vsjqld">
                    {COMPANY.email}
                  </a>
                </span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} data-id="element-202" data-mp="3cghdpbqu3eqy">
          <form onSubmit={submit} className="glass-strong rounded-[2.5rem] p-8 shadow-glass sm:p-10" data-id="element-203" data-mp="38c9jx05ir8lv">
            <div className="grid gap-5" data-id="element-204" data-mp="1nu7et2rvutwy">
              {fields.map(field => <div key={field.name} className="group" data-id="element-205" data-mp="1rvavtslo2vzr">
                  <label htmlFor={field.name} className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate" data-id="element-206" data-mp="1wvfcbimkrqpz">
                    {field.label}
                  </label>
                  <input id={field.name} type={field.type} required={field.name !== 'phone'} placeholder={field.placeholder} value={form[field.name]} onChange={event => setForm({
                ...form,
                [field.name]: event.target.value
              })} className="w-full rounded-2xl border border-royal/15 bg-white/70 px-4 py-3.5 text-sm text-navy outline-none transition-all placeholder:text-slate/60 focus:border-royal focus:shadow-[0_0_0_4px_rgba(0,87,255,0.12)]" data-id="element-207" data-mp="1482o740rnauk" />
                </div>)}
              <div data-id="element-208" data-mp="s4gstot8lp4r">
                <label htmlFor="message" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate" data-id="element-209" data-mp="s504wow7hjav">
                  How can we help?
                </label>
                <textarea id="message" required rows={4} placeholder="Tell us about your project or emergency..." value={form.message} onChange={event => setForm({
                ...form,
                message: event.target.value
              })} className="w-full resize-none rounded-2xl border border-royal/15 bg-white/70 px-4 py-3.5 text-sm text-navy outline-none transition-all placeholder:text-slate/60 focus:border-royal focus:shadow-[0_0_0_4px_rgba(0,87,255,0.12)]" data-id="element-210" data-mp="3vlp8c20hscwb" />
              </div>
              <motion.button type="submit" data-cursor="button" disabled={status !== 'idle'} animate={{
              backgroundColor: status === 'done' ? '#19C37D' : '#0057FF',
              width: status === 'idle' ? '100%' : '64px'
            }} transition={{
              duration: 0.5,
              ease: [0.22, 1, 0.36, 1]
            }} className="mx-auto flex h-14 items-center justify-center gap-2 self-center overflow-hidden rounded-full font-semibold text-white shadow-glow" data-id="element-211" data-mp="32sbzr979kbce">
                {status === 'idle' && <span data-id="element-212" data-mp="49es516qto0f">Schedule Free Inspection</span>}
                {status === 'done' && <motion.span initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} data-id="element-213" data-mp="8zlip6ktfilb">
                    <CheckIcon className="h-6 w-6" strokeWidth={3} data-id="element-214" data-mp="91oiujfoms3r" />
                  </motion.span>}
              </motion.button>
              {status === 'done' && <p role="status" className="text-center text-sm font-medium text-success" data-id="element-215" data-mp="20a6t1fa14lls">
                  Your email app is ready with this request addressed to {COMPANY.email}.
                </p>}
            </div>
          </form>
        </Reveal>
      </div>
    </section>;
}